/** @jsxImportSource @emotion/react */
import { css } from "@emotion/react";
import styled from "@emotion/styled";
import { useRouter } from "next/router";
import { useEffect, useRef, useState } from "react";
import { toast } from "react-toastify";
import { BillingAPI } from "../../../api/Billing";
import { SERVER_URL } from "../../../config";
import { appendGTMHeader, removeGTMScript } from "../../../utils/GoogleGTM";
import {
  localStorageGetItem,
  localStorageSetItem,
} from "../../../utils/LocalStorage";
import { validationEmail } from "../../../utils/Validation";
import StyledButton from "../../StyledButton";
import StyledInput from "../../StyledInput";
import { IFormSignup, IFormSignupValidation, SignupFormSection } from "../Form";

const FormContainer = styled.div`
  width: 560px;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: center;
  margin-top: 40px;

  > div + div {
    margin-top: 24px;
  }
`;

const FormWrapper = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: center;

  .label {
    font-size: 16px;
    font-weight: 600;
    color: #222;
    margin-bottom: 8px;
  }

  .single-input-wrapper {
    width: 100%;
    display: flex;
    align-items: center;
    justify-content: space-between;
  }
`;

const ButtonsWrapper = styled.div`
  width: 100%;
  margin-top: 48px !important;
  display: flex;
  align-items: center;
  justify-content: center;

  > button {
    width: 100%;
  }

  > button + button {
    margin-left: 16px;
  }
`;

const TooltipText = styled.span`
  font-size: 14px;
  font-weight: 600;
  letter-spacing: 0px;
  color: #ffffff;
`;

interface FirstFormProps {
  setSelectedForm: React.Dispatch<React.SetStateAction<SignupFormSection>>;
  form: IFormSignup;
  handleChangeForm: (
    key: string,
    value: string,
    secondKey?: string,
    secondValue?: string
  ) => void;
  formValidation: IFormSignupValidation;
  handleChangeFormValidation: (key: string, value: boolean) => void;
}

export default function FirstForm({
  setSelectedForm,
  form,
  handleChangeForm,
  formValidation,
  handleChangeFormValidation,
}: FirstFormProps) {
  const router = useRouter();
  const { ref } = router.query;
  const affiliateReferralCode = useRef(null);

  useEffect(() => {
    if (ref) {
      const affileateRef = (ref as string)?.split("?")[0];
      affiliateReferralCode.current = affileateRef;
      localStorageSetItem("REFERRAL_CODE", affileateRef);
    } else {
      const referralCode = localStorageGetItem("REFERRAL_CODE");
      if (referralCode) {
        affiliateReferralCode.current = referralCode;
      }
    }
  }, [ref]);

  useEffect(() => {
    const sc = appendGTMHeader();

    return () => {
      removeGTMScript(sc);
    };
  }, []);

  const [isLoading, setIsLoading] = useState(false);

  function isDisabled() {
    return (
      form.firstName === "" ||
      form.lastName === "" ||
      form.email === "" ||
      !formValidation.isEmailValid
    );
  }

  async function handleClick() {
    setIsLoading(true);

    try {
      if (!validationEmail(form.email)) {
        toast("Incorrect email format", { type: "error" });
        handleChangeFormValidation("isEmailValid", false);
        return;
      }

      const { firstName, lastName, email } = form;

      const emailExists = await checkIfEmailExists(email);

      let provider = null;
      if (window.location.href.includes("provider=google")) {
        provider = "google";
      }

      if (emailExists) {
        handleChangeFormValidation("isEmailValid", false);
        toast("Account with this email already exists", { type: "error" });
        return;
      }

      const response = await fetch(
        `${SERVER_URL}/auth/create_account_contact`,
        {
          method: "POST",
          headers: {
            Accept: "application/json",
            "Content-type": "application/json",
          },
          body: JSON.stringify({
            email,
            firstName,
            lastName,
            ...(affiliateReferralCode.current
              ? { affiliateReferralCode: affiliateReferralCode.current }
              : {}),
            provider,
          }),
        }
      );

      const responseJSON = await response.json();

      if (responseJSON.data.exists === true) {
        toast("You're already a subscriber", { type: "info" });
        setSelectedForm(SignupFormSection.Create);
        return;
      } else {
        setSelectedForm(SignupFormSection.Create);
      }
    } catch (error) {
      toast("Unexpected error", { type: "error" });
    } finally {
      setIsLoading(false);
    }
  }

  async function checkIfEmailExists(email: string): Promise<boolean> {
    const response: Response = await fetch(
      `${SERVER_URL}/auth/check_user_email`,
      {
        method: "POST",
        headers: {
          Accept: "application/json",
          "Content-type": "application/json",
        },
        body: JSON.stringify({ email }),
      }
    );

    if (response.status === 200) {
      const responseObject = await response.json();
      return responseObject.data.exists;
    } else {
      return false;
    }
  }

  return (
    <FormContainer>
      <div className="flex-input">
        <FormWrapper>
          <div className="label">First Name</div>
          <StyledInput
            value={form.firstName}
            onChange={(e) => {
              handleChangeForm("firstName", e.target.value.trim());

              handleChangeFormValidation("isFirstNameValid", true);
            }}
            placeholder="Enter first name"
            css={css`
              border-color: ${formValidation.isFirstNameValid
                ? "#dddddd"
                : "red"};
            `}
          />
        </FormWrapper>

        <FormWrapper>
          <div className="label">Last Name</div>
          <StyledInput
            value={form.lastName}
            onChange={(e) => {
              handleChangeForm("lastName", e.target.value.trim());

              handleChangeFormValidation("isLastNameValid", true);
            }}
            placeholder="Enter last name"
            css={css`
              border-color: ${formValidation.isLastNameValid
                ? "#dddddd"
                : "red"};
            `}
          />
        </FormWrapper>
      </div>

      <FormWrapper>
        <div className="label">Email</div>
        <StyledInput
          key={"email_validation"}
          data-tip
          data-for={"email_validation"}
          value={form.email}
          onChange={(e) => {
            handleChangeForm("email", e.target.value.trim().toLowerCase());
            handleChangeFormValidation("isEmailValid", true);
          }}
          placeholder="Enter email address"
          css={css`
            border-color: ${formValidation.isEmailValid ? "#dddddd" : "red"};
          `}
        />
      </FormWrapper>

      <ButtonsWrapper>
        <StyledButton onClick={() => router.push("/")} inverted>
          Back
        </StyledButton>
        <StyledButton
          isLoading={isLoading}
          disable={isDisabled()}
          onClick={handleClick}
        >
          Next
        </StyledButton>
      </ButtonsWrapper>
    </FormContainer>
  );
}
